import React from 'react';
import { faFont, faBold, faItalic } from '@fortawesome/pro-regular-svg-icons';
import { faBold as faBoldSelect, faItalic as faItalicSelect } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MultiOptionButton from 'components/common/MultiOptionButton';
import textAligns from 'constants/textAligns';
import CustomAlignLabel from 'components/common/CustomAlignLabel';
import ToolBarColorPicker from '../../components/ToolbarColorPicker';
import FontFamilySelect from '../../components/FontFamilySelect';
import SelectFontSize from '../../components/SelectFontSize';

/**
 * @param {Object} props
 * @param {import('actions/scene.types').CalendarShape} props.shape Calendar shape being edited.
 * @param {import('actions/scene.types').CalendarShapeChangeHandler} props.onChange Applies a partial calendar update.
 * @param {Object} [props.toolbarRef] Toolbar element ref used to position popovers.
 * @param {string} [props.className] Optional class for the labelled admin layout.
 * @returns {React.ReactElement}
 */
export const DayNumberFields = ({ shape, onChange, toolbarRef, className }) => {
  const alignment = (
    <MultiOptionButton
      options={textAligns}
      onChange={(textAlign) => onChange({ dayNumberTextAlign: textAlign })}
      value={shape.dayNumberTextAlign || (shape.calendarType === 2 ? 'center' : 'right')}
      LabelComponent={CustomAlignLabel}
    />
  );
  const fontSize = <SelectFontSize value={shape.dayFontSize} onChange={(value) => onChange({ dayFontSize: value })} />;
  const fontFamily = (
    <FontFamilySelect initialValue={shape.fontFamily} onChange={(value) => onChange({ fontFamily: value })} />
  );
  const style = (
    <>
      <div className="icon-button-container" onClick={() => onChange({ isBold: !shape.isBold })}>
        <FontAwesomeIcon.Memo icon={shape.isBold ? faBoldSelect : faBold} className="icon-button" />
      </div>
      <div className="icon-button-container" onClick={() => onChange({ isItalic: !shape.isItalic })}>
        <FontAwesomeIcon.Memo icon={shape.isItalic ? faItalicSelect : faItalic} className="icon-button" />
      </div>
    </>
  );
  const colour = (
    <ToolBarColorPicker
      initialValue={shape.mainColor}
      toolbarRef={toolbarRef}
      onChange={(value) => onChange({ mainColor: value })}
      icon={faFont}
      alignPickerLeft={!!className}
    />
  );

  if (className) {
    /**
     * @param {string} label
     * @param {React.ReactNode} control
     * @param {string} [controlClassName]
     */
    const labelledField = (label, control, controlClassName = 'typography-field-control') => (
      <div className="typography-field">
        <div className="typography-field-label">{label}</div>
        <div className={controlClassName}>{control}</div>
      </div>
    );

    return (
      <div className={className}>
        {labelledField('Text alignment', alignment)}
        {labelledField('Font size', fontSize)}
        {labelledField('Font family', fontFamily)}
        {labelledField('Font style', style, 'typography-field-control typography-style-controls')}
        {labelledField('Current month colour', colour, 'typography-field-control typography-colour-control')}
      </div>
    );
  }

  return (
    <>
      <div className="text-align-buttons">{alignment}</div>
      {fontSize}
      {fontFamily}
      {style}
      {colour}
    </>
  );
};

export default DayNumberFields;
